import { type FC } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { type UserIntegrations } from '../../../../models/Scheduling'
import { useIntegrations } from '../../../Integration/useIntegrations'
import {
  AvailabilityCalendarState,
  type PrimaryAvailabilityCalendarState,
  resolveAvailabilityCalendarStatus,
  type UnhealthyCalendar,
} from './availabilityCalendarStatus'
import { integrationConnectPath, SCHEDULING_INTEGRATIONS_PATH } from './schedulingIntegrationStatus'

const LINK_CLASS = 'paragraph-small zocks-text-primary text-decoration-none text-nowrap'

const BANNER_CONTENT: Record<Exclude<AvailabilityCalendarState, AvailabilityCalendarState.ISSUE>, { icon: string; message: string; action: string }> = {
  [AvailabilityCalendarState.CONNECTED]: {
    icon: 'bi bi-calendar-event',
    message: 'Availability uses all connected calendars',
    action: 'Manage calendars',
  },
  [AvailabilityCalendarState.NO_CALENDAR]: {
    icon: 'bi bi-calendar-plus',
    message: 'Connect a calendar before clients can book these hours',
    action: 'Connect calendar',
  },
  [AvailabilityCalendarState.NOT_SELECTED]: {
    icon: 'bi bi-calendar-plus',
    message: 'Select a scheduling calendar before clients can book these hours',
    action: 'Select calendar',
  },
}

const PrimaryBanner: FC<{ primary: PrimaryAvailabilityCalendarState }> = ({ primary }) => {
  if (primary.state === AvailabilityCalendarState.ISSUE) {
    return (
      <div
        role='status'
        aria-live='polite'
        className='d-flex align-items-center gap-2 px-3 zocks-py-0.75 rounded-3 zocks-bg-color-white zocks-border-color-warning-dark'
      >
        <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark flex-shrink-0' aria-hidden='true' />
        <span className='paragraph-small zocks-text-gray-900 flex-grow-1'>Clients can&apos;t book right now. Your scheduling calendar isn&apos;t syncing.</span>
        <Link to={integrationConnectPath(primary.serviceId)} className={LINK_CLASS}>
          Reconnect
        </Link>
      </div>
    )
  }

  const { icon, message, action } = BANNER_CONTENT[primary.state]

  return (
    <div role='status' aria-live='polite' className='d-flex align-items-center gap-2 zocks-p-0.75 rounded-3 zocks-bg-color-gray-100'>
      <i className={classNames('zocks-text-gray-900 flex-shrink-0', icon)} aria-hidden='true' />
      <span className='paragraph-small zocks-text-gray-900 flex-grow-1'>{message}</span>
      <Link to={SCHEDULING_INTEGRATIONS_PATH} className={LINK_CLASS}>
        {action}
      </Link>
    </div>
  )
}

export const UnhealthyCalendarNotice: FC<{ calendars: UnhealthyCalendar[] }> = ({ calendars }) => {
  if (calendars.length === 0) {
    return null
  }

  const single = calendars.length === 1 ? calendars[0] : undefined
  const message = single
    ? `${single.label} isn't syncing. Clients could book over events on that calendar.`
    : "More calendars aren't syncing. Clients could book over events on those calendars."

  return (
    <div role='status' aria-live='polite' className='d-flex align-items-center gap-2'>
      <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark flex-shrink-0' aria-hidden='true' />
      <span className='paragraph-small zocks-text-gray-900 flex-grow-1'>{message}</span>
      <Link to={single ? integrationConnectPath(single.serviceId) : SCHEDULING_INTEGRATIONS_PATH} className={LINK_CLASS}>
        Reconnect
      </Link>
    </div>
  )
}

export const AvailabilityCalendarBanner: FC<{ integrations: UserIntegrations }> = ({ integrations }) => {
  const { isIntegrationAllowed, isLoading } = useIntegrations()

  if (isLoading) {
    return null
  }

  const { primary, unhealthyOtherCalendars } = resolveAvailabilityCalendarStatus(integrations, isIntegrationAllowed)

  return (
    <>
      <PrimaryBanner primary={primary} />
      <UnhealthyCalendarNotice calendars={unhealthyOtherCalendars} />
    </>
  )
}
